/**
 * EmbedWidget.tsx
 * Widget embed code generator for admins
 * Path: components/admin-tabs/reviews/EmbedWidget.tsx
 */

import React, { useState } from 'react';
import { UserProfile } from '../../../types';
import { Copy, Check, Code, FileText } from 'lucide-react';

interface EmbedWidgetProps {
  user: UserProfile;
}

export const EmbedWidget: React.FC<EmbedWidgetProps> = ({ user }) => {
  const [copied, setCopied] = useState(false);

  // Extract shop domain from user's integrations
  const shopDomain = user.integrations?.platformConfig?.domain 
    ? `${user.integrations.platformConfig.domain}`
    : 'your-store.myshopify.com';

  const embedCode = `<!-- Powerpack Reviews Widget -->
<div 
    id="powerpack-reviews"
    data-admin-id="${user.id}"
    data-product-id="{{ product.handle }}"
    data-shop="${shopDomain}"
    data-layout="grid"
    data-reviews-per-page="6"
    data-star-color="#fbbf24"
    data-show-verified-badge="true"
></div>
<script src="https://powerpack.techsoftwares.in/powerpack-widget.js"></script>`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      alert('Failed to copy to clipboard');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Code className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">
              How to Install the Widget
            </h3>
            <p className="text-sm text-blue-700">
              Copy the code below and paste it into your Shopify product page template using a Custom Liquid block.
            </p>
          </div>
        </div>
      </div>

      {/* Embed Code */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Widget Embed Code
          </label>
          <button
            onClick={handleCopy}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              copied
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {copied ? (
              <>
                <Check className="w-4 h-4" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Copy Code
              </>
            )}
          </button>
        </div>

        <div className="relative">
          <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm font-mono">
            <code>{embedCode}</code>
          </pre>
        </div>
      </div>

      {/* Important Notes */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Important Notes
        </h4>
        <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
          <li>
            <code className="bg-yellow-100 px-1 rounded">{'{{ product.handle }}'}</code> automatically gets the current product ID
          </li>
          <li>
            <code className="bg-yellow-100 px-1 rounded">{'{{ shop.domain }}'}</code> automatically gets your shop domain
          </li>
          <li>Do NOT replace these with actual values - keep the double curly braces</li>
          <li>The widget will work on all product pages with this single code snippet</li>
        </ul>
      </div>

      {/* Installation Steps */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="font-semibold text-gray-900 mb-4">Installation Steps</h4>
        <ol className="space-y-4 text-sm text-gray-700">
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
              1
            </span>
            <div>
              <p className="font-medium text-gray-900">Open Shopify Theme Editor</p>
              <p className="text-gray-600">Go to Online Store → Themes → Customize</p>
            </div>
          </li>

          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
              2
            </span>
            <div>
              <p className="font-medium text-gray-900">Navigate to Product Page</p>
              <p className="text-gray-600">Select "Products" → "Default product" from the dropdown</p>
            </div>
          </li>

          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
              3
            </span>
            <div>
              <p className="font-medium text-gray-900">Add Custom Liquid Block</p>
              <p className="text-gray-600">Click "Add block" → Select "Custom Liquid"</p>
            </div>
          </li>

          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
              4
            </span>
            <div>
              <p className="font-medium text-gray-900">Paste the Embed Code</p>
              <p className="text-gray-600">Copy the code above and paste it into the Custom Liquid block</p>
            </div>
          </li>

          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
              5
            </span>
            <div>
              <p className="font-medium text-gray-900">Position and Save</p>
              <p className="text-gray-600">Drag the block to your desired position (usually after product description) and click Save</p>
            </div>
          </li>
        </ol>
      </div>

      {/* Testing */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h4 className="font-semibold text-green-900 mb-2">Testing the Widget</h4>
        <p className="text-sm text-green-800 mb-2">
          After installation, visit any product page on your store to see the reviews widget.
        </p>
        <p className="text-sm text-green-800">
          Make sure you have approved reviews for that product in the <strong>Moderation</strong> tab.
        </p>
      </div>

      {/* Advanced Settings Info */}
      <div className="border-t border-gray-200 pt-6">
        <h4 className="font-semibold text-gray-900 mb-3">Customization Options</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="bg-gray-50 rounded p-3">
            <p className="font-medium text-gray-900 mb-1">Layout</p>
            <code className="text-xs text-gray-600">data-layout="grid"</code>
            <p className="text-xs text-gray-500 mt-1">Options: grid, list, carousel</p>
          </div>

          <div className="bg-gray-50 rounded p-3">
            <p className="font-medium text-gray-900 mb-1">Reviews Per Page</p>
            <code className="text-xs text-gray-600">data-reviews-per-page="6"</code>
            <p className="text-xs text-gray-500 mt-1">Options: 3, 6, 9, 12</p>
          </div>

          <div className="bg-gray-50 rounded p-3">
            <p className="font-medium text-gray-900 mb-1">Star Color</p>
            <code className="text-xs text-gray-600">data-star-color="#fbbf24"</code>
            <p className="text-xs text-gray-500 mt-1">Any hex color code</p>
          </div>

          <div className="bg-gray-50 rounded p-3">
            <p className="font-medium text-gray-900 mb-1">Verified Badge</p>
            <code className="text-xs text-gray-600">data-show-verified-badge="true"</code>
            <p className="text-xs text-gray-500 mt-1">Options: true, false</p>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          💡 Tip: You can customize these in the <strong>Display Settings</strong> tab and the widget will update automatically!
        </p>
      </div>
    </div>
  );
};
